import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';

import { translate as t } from 'hooks/useTranslations';
import useAccreditationVersion from '../../../_data_sources/AccreditationVersions/useAccreditationVersion';
import { publishAccreditationVersion } from '../../../_data_sources/AccreditationVersions/manageAccreditationVersions';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import { Box, Button, CircularProgress, Stack, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { QuickSearch } from 'components/_atoms';
import Modal from 'components/_layouts/Modal';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import datetimeHelper from 'helpers/datetimeHelper';
import { enqueueSnackbar } from 'notistack';
import { ServerErrorResponse } from 'data_sources/constants';
import errorHelper from 'helpers/errorHelper';

import { JurisdictionWithPublishedState } from '../../../_types/awb';

const ReleaseVersion: React.FC = () => {
  const { id: accreditationBodyId, versionId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [availableJurisdictions, setAvailableJurisdictions] = useState<JurisdictionWithPublishedState[]>([]);
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<JurisdictionWithPublishedState[]>([]);
  const [jurisdictionSearch, setJurisdictionSearch] = useState<string>('');
  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [notificationMode, setNotificationMode] = useState<'silent' | 'notify'>('silent');

  const versionQuery = useAccreditationVersion(accreditationBodyId ?? '', versionId ?? '');

  useEffect(() => {
    if (versionQuery.data?.jurisdictions) {
      setAvailableJurisdictions(versionQuery.data.jurisdictions as JurisdictionWithPublishedState[]);
    }
  }, [versionQuery.data]);

  useEffect(() => {
    setSelectedJurisdictions(availableJurisdictions.filter((j) => rowSelectionModel.includes(j.id)));
  }, [availableJurisdictions, rowSelectionModel]);

  const handleReleaseClick = () => {
    const hasNonDraftJurisdictions = selectedJurisdictions.some(
      (jurisdiction) => jurisdiction.publishedState !== 'DRAFT'
    );

    if (hasNonDraftJurisdictions) {
      setShowConfirmModal(true);
    } else {
      handleConfirmRelease();
    }
  };

  const handleConfirmRelease = () => {
    setShowConfirmModal(false);
    setIsSaving(true);
    const jurisdictionIds = selectedJurisdictions.map((j) => j.id);
    const publishData = {
      jurisdictions: jurisdictionIds,
      republishNotificationRequired: notificationMode === 'notify',
    };

    publishAccreditationVersion(
      accreditationBodyId ?? '',
      versionId ?? '',
      publishData,
      () => {
        setIsSaving(false);
        enqueueSnackbar(
          parse(
            t('awb.accreditingBodyVersions.release.successToast', '', {
              versionName: versionQuery.data?.version || '',
              jurisdictionCount: selectedJurisdictions.length.toString(),
            })
          ),
          {
            variant: 'success',
            autoHideDuration: 6000,
          }
        );
        queryClient.invalidateQueries(['accreditation_versions', accreditationBodyId]);
        queryClient.invalidateQueries(['accreditation_version', versionId]);
        navigate(`/admin/awb/accrediting-bodies/${accreditationBodyId}/versions`);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        console.error('Release error:', errors);
        errorHelper.enqueueErrors(errors);
      }
    );
  };

  const selectJurisdictionsColumns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('awb.accreditingBodyVersions.release.selectJurisdictions.columns.name'),
        flex: 3,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.name}</span>;
        },
        valueGetter: (value: any, row: any) => {
          return row.name;
        },
      },
      abbreviation: {
        header: t('awb.accreditingBodyVersions.release.selectJurisdictions.columns.abbreviation'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.abbreviation}</span>;
        },
      },
      publishDate: {
        header: t('awb.accreditingBodyVersions.release.selectJurisdictions.columns.publishDate'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{datetimeHelper.formatDate(row.lastPublishedAt) || 'N/A'}</span>;
        },
      },
      lastModified: {
        header: t('awb.accreditingBodyVersions.release.selectJurisdictions.columns.lastModified'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{datetimeHelper.formatDate(row.updatedAt)}</span>;
        },
      },
    }),
    [t]
  );

  const filteredJurisdictions = useMemo(() => {
    if (!jurisdictionSearch) return availableJurisdictions;

    return availableJurisdictions.filter(
      (jurisdiction) =>
        jurisdiction.name.toLowerCase().includes(jurisdictionSearch.toLowerCase()) ||
        jurisdiction.abbreviation.toLowerCase().includes(jurisdictionSearch.toLowerCase())
    );
  }, [availableJurisdictions, jurisdictionSearch]);

  return (
    <TakeoverLayout
      backUrl={`/admin/awb/accrediting-bodies/${accreditationBodyId}/versions`}
      pageTitle={versionQuery.data?.version || ''}
      pageToolbarLeft={
        <Stack
          direction='row'
          spacing={3}
        >
          <QuickSearch
            id='jurisdiction_search'
            initialValue={jurisdictionSearch}
            onChange={(val) => setJurisdictionSearch(val)}
            placeholder={t('awb.accreditingBodyVersions.release.search')}
          />
        </Stack>
      }
      pageToolbarRight={
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', pb: 2, width: '100%' }}>
          <Button
            variant='outlined'
            color='neutral'
            onClick={() => navigate(`/admin/awb/accrediting-bodies/${accreditationBodyId}/versions`)}
            size='small'
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleReleaseClick}
            disabled={isSaving || selectedJurisdictions.length === 0}
            component='button'
            type='submit'
            variant='contained'
            endIcon={isSaving ? <CircularProgress size={16} /> : null}
            size='small'
          >
            {t('awb.accreditingBodyVersions.release.confirm')}
          </Button>
        </Box>
      }
    >
      <Stack
        spacing={4}
        sx={{ height: '100%' }}
      >
        <Box>
          <Typography
            variant='h6'
            gutterBottom
          >
            {t('awb.accreditingBodyVersions.release.title')}
          </Typography>
        </Box>

        <GridSection
          columns={selectJurisdictionsColumns}
          data={filteredJurisdictions}
          query={{
            data: { pages: [{ rows: filteredJurisdictions, count: filteredJurisdictions.length }] },
            isLoading: versionQuery.isLoading,
            isError: versionQuery.isError,
            error: versionQuery.error,
          }}
          totalCount={filteredJurisdictions.length}
          gridProps={{
            checkboxSelection: true,
            onRowSelectionModelChange: (newRowSelectionModel: GridRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            },
            rowSelectionModel,
            filterModel: {
              items: [{ field: 'name', operator: 'contains', value: jurisdictionSearch }],
            },
          }}
        />
      </Stack>

      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={t('awb.accreditingBodyVersions.release.modal.title')}
        subtitle={t('awb.accreditingBodyVersions.release.modal.subtitle')}
        size='sm'
        actions={
          <>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => setShowConfirmModal(false)}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleConfirmRelease}
              disabled={isSaving}
              endIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {t('awb.accreditingBodyVersions.release.modal.confirm')}
            </Button>
          </>
        }
      >
        <Stack spacing={3}>
          <Typography variant='body1'>{t('awb.accreditingBodyVersions.release.modal.description')}</Typography>

          <RadioGroup
            value={notificationMode}
            onChange={(e) => setNotificationMode(e.target.value as 'silent' | 'notify')}
          >
            <FormControlLabel
              value='silent'
              control={<Radio />}
              label={
                <Stack>
                  <Typography
                    variant='body1'
                    fontWeight={600}
                  >
                    {t('awb.accreditingBodyVersions.release.modal.silent.title')}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.accreditingBodyVersions.release.modal.silent.description')}
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              value='notify'
              control={<Radio />}
              label={
                <Stack>
                  <Typography
                    variant='body1'
                    fontWeight={600}
                  >
                    {t('awb.accreditingBodyVersions.release.modal.notify.title')}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('awb.accreditingBodyVersions.release.modal.notify.description')}
                  </Typography>
                </Stack>
              }
            />
          </RadioGroup>
        </Stack>
      </Modal>
    </TakeoverLayout>
  );
};

export default ReleaseVersion;
